/**
 * CommentForm Component
 * Form for creating new comments with optional file attachments and @mentions
 */

import { useState, useRef, useCallback } from 'react'
import {
  Send,
  Lock,
  Paperclip,
  X,
  FileText,
  Loader2
} from 'lucide-react'
import { MentionInput } from '../MentionInput'

interface PendingFile {
  id: string
  file: File
  preview?: string
}

interface CommentFormProps {
  userRole: 'admin' | 'subcontractor'
  onSubmit: (content: string, isInternal: boolean, files: File[], mentionedUserIds: string[]) => Promise<void>
  isSubmitting?: boolean
  assignmentId?: string  // For TGs: required to see colleagues on same assignment
}

export function CommentForm({
  userRole,
  onSubmit,
  isSubmitting = false,
  assignmentId
}: CommentFormProps) {
  const [content, setContent] = useState('')
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [submittingType, setSubmittingType] = useState<'public' | 'internal' | null>(null)
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const canSetInternal = userRole === 'admin'
  const maxFileSize = 10 * 1024 * 1024 // 10 MB
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ]

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])

    const validFiles = files.filter(file => {
      if (file.size > maxFileSize) {
        alert(`${file.name} är för stor. Max storlek är 10 MB.`)
        return false
      }
      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name} har ett format som inte stöds.`)
        return false
      }
      return true
    })

    const newPendingFiles: PendingFile[] = validFiles.map(file => ({
      id: Math.random().toString(36).substring(7),
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }))

    setPendingFiles(prev => [...prev, ...newPendingFiles])

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removePendingFile = (id: string) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === id)
      if (file?.preview) {
        URL.revokeObjectURL(file.preview)
      }
      return prev.filter(f => f.id !== id)
    })
  }

  const handleSubmit = async (isInternal: boolean) => {
    if (!content.trim() && pendingFiles.length === 0) return

    setSubmittingType(isInternal ? 'internal' : 'public')
    try {
      await onSubmit(
        content.trim(),
        isInternal,
        pendingFiles.map(pf => pf.file),
        mentionedUserIds
      )

      // Clear form on success
      setContent('')
      setMentionedUserIds([])
      pendingFiles.forEach(pf => {
        if (pf.preview) URL.revokeObjectURL(pf.preview)
      })
      setPendingFiles([])
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setSubmittingType(null)
    }
  }

  const handleMentionsChange = useCallback((userIds: string[]) => {
    setMentionedUserIds(userIds)
  }, [])

  const isDisabled = isSubmitting || submittingType !== null || (!content.trim() && pendingFiles.length === 0)

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="bg-white dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700 p-4">
      {/* MentionInput with @autocomplete */}
      <MentionInput
        value={content}
        onChange={setContent}
        onMentionsChange={handleMentionsChange}
        placeholder="Skriv en kommentar... Använd @ för att nämna någon"
        rows={3}
        disabled={isSubmitting || submittingType !== null}
        assignmentId={assignmentId}
      />

      {/* Pending Files */}
      {pendingFiles.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-1 text-xs text-nature-500">
            <Paperclip className="h-3 w-3" />
            <span>{pendingFiles.length} {pendingFiles.length === 1 ? 'fil vald' : 'filer valda'}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {pendingFiles.map((pf) => (
              <div
                key={pf.id}
                className="relative group flex items-center gap-2 p-2 bg-nature-100 dark:bg-nature-700 rounded-lg"
              >
                {pf.preview ? (
                  <img
                    src={pf.preview}
                    alt={pf.file.name}
                    className="w-10 h-10 object-cover rounded"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-nature-200 dark:bg-nature-600 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-nature-500" />
                  </div>
                )}
                <div className="max-w-[120px]">
                  <p className="text-xs font-medium text-nature-900 dark:text-nature-100 truncate">
                    {pf.file.name}
                  </p>
                  <p className="text-xs text-nature-500">
                    {formatFileSize(pf.file.size)}
                  </p>
                </div>
                <button
                  onClick={() => removePendingFile(pf.id)}
                  className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex items-center justify-between gap-2">
        {/* File upload */}
        <div className="flex items-center">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedTypes.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isDisabled}
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 text-sm text-nature-600 dark:text-nature-400 hover:text-nature-800 dark:hover:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors disabled:opacity-50"
            title="Bifoga fil"
          >
            <Paperclip className="h-4 w-4" />
            <span className="hidden sm:inline">Bifoga</span>
          </button>
        </div>

        {/* Submit buttons */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {/* Internal comment button - only for admins */}
          {canSetInternal && (
            <button
              onClick={() => handleSubmit(true)}
              disabled={isDisabled}
              className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-4 sm:py-2 text-sm bg-nature-500 text-white rounded-lg hover:bg-nature-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Intern kommentar"
            >
              {submittingType === 'internal' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Lock className="h-4 w-4" />
                  <span className="hidden sm:inline">Intern</span>
                </>
              )}
            </button>
          )}

          {/* Public comment button */}
          <button
            onClick={() => handleSubmit(false)}
            disabled={isDisabled}
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-4 sm:py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Skicka kommentar"
          >
            {submittingType === 'public' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">Skicka</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Help text */}
      <p className="mt-2 text-xs text-nature-500">
        Använd @ för att nämna någon. Tryck Ctrl+Enter för att skicka. Max 10 MB per fil.
      </p>
    </div>
  )
}
